import { Dispatch, MiddlewareAPI } from "redux";
import ApiUrls from "../../../constants/ApiUrls";
import CacheTime from "../../../constants/CacheTime";
import MapZoom from "../../../constants/MapZoom";
import MiddlewareEntities from "../../../constants/MiddlewareEntities";
import { getFromCacheByKey } from "../../../utilites/cache";
import { getActiveLanguage, isCheckout } from "../../../utilites/util";
import { apiRequest } from "../api/actions";
import { IApiRequestActionMeta } from "../api/types";
import { getAvailabilityProduct } from "../availabilityProduct/actions";
import { setApiResponseInCache } from "../cache/actions";
import { createErrorNotification } from "../notification/actions";
import { AppState } from "../rootReducers";
import { setLoader } from "../ui/actions";
import {
  getAllStoresMapAction,
  getCitiesAction,
  getClosestStoresAction,
  getUserGeoLocationAction,
  setCitiesAction,
  setFiveClosestStatusAction,
  setMapCenterAndZoomAction,
  setStoreFilterAction,
  setStoresMapAction,
  setStoresMapVisibilityAction,
  setUserGeolocationAction
} from "./actions";
import { IStore } from "./types";
import { ICheckoutCurrentStepDataItem } from "../../../entities/manhattan/order/types";
import { setSelectedStoreIdAction } from "../campaign/campaignOverview/actions";
import { OrderTypes } from "../../../constants/OrderTypes";

type StoresAPIRequestPayload = {
  data: {
    stores: IStore[]
  },
  meta: IApiRequestActionMeta
};

abstract class IStoresMapMiddlewareProxy {

  public static getAllStores: (next: Dispatch, params: any) => void;
  public static getOnlyStores: (next: Dispatch, params: any) => void;
  public static getStoresAPISucess: (next: Dispatch, payload: StoresAPIRequestPayload) => void;
  public static getStoresAPIError: (next: Dispatch, payload: StoresAPIRequestPayload) => void;
  public static setClosestStoresStatus: (store: MiddlewareAPI, showClosestStoresStatus: boolean, next: Dispatch) => void;
  public static getClosestStores: (state: AppState, next: Dispatch) => void;
  public static getClosestStoresAPISuccess: (next: Dispatch, payload: StoresAPIRequestPayload) => void;
  public static getUserGeoLocation: (next: Dispatch) => void;
  public static getCities: (next: Dispatch, payload: IStore[]) => void;
  public static setActiveStoreId: (state: AppState, next: Dispatch, payload: string | number) => void;
  public static getMultipleProductStoreAvailability: (state: AppState, next: Dispatch, params: any[]) => void;
  public static close: (next: Dispatch) => void;

}

export default class StoresMapMiddlewareProxy extends IStoresMapMiddlewareProxy {

  public static getAllStores(next: Dispatch, params: any): void {
    next(getUserGeoLocationAction());

    const stores = getFromCacheByKey(MiddlewareEntities.STORES_MAP + JSON.stringify(params));

    if (stores) {
      next(setStoresMapAction(stores));
      return;
    }

    next(apiRequest(
      null,
      'GET', `${ApiUrls.STORES}/all-stores`,
      { ...params },
      null,
      MiddlewareEntities.STORES_MAP,
      {}),
    );
    next(setLoader(true, MiddlewareEntities.STORES_MAP));
  }

  public static getStoresAvailabilityAndSetActiveIdAction(store: MiddlewareAPI, next: Dispatch, params: any[]): void {
    const state = store.getState();

    const requestData = state.manhattanOrder.checkoutData.steps.current.data.items
      .map(({ productId, colorId }: ICheckoutCurrentStepDataItem) => {
        return {
          item: {
            productId,
            colorId
          },
          quantity: 1,
        };
      });

    next(apiRequest(
      null,
      'POST', `${ApiUrls.STORES}/all-stores-for-multiple-products`,
      { ...params },
      requestData,
      MiddlewareEntities.STORES_MAP_MULTIPLE,
      {}),
    );
  }

  public static getMultipleProductStoreAvailability(state: AppState, next: Dispatch, params: any): void {
    next(setStoresMapVisibilityAction(true));
    next(getUserGeoLocationAction());

    const requestData = params.items || state.manhattanOrder.checkoutData.steps.current.data.items
      .map(({ productId, colorId }: ICheckoutCurrentStepDataItem) => {
        return {
          item: {
            productId,
            colorId
          },
          quantity: 1,
        };
      });

    next(apiRequest(
      null,
      'POST', `${ApiUrls.STORES}/all-stores-for-multiple-products`,
      { lang: params.lang },
      requestData,
      MiddlewareEntities.STORES_MAP,
      {}),
    );
    next(setLoader(true, MiddlewareEntities.STORES_MAP));
  }

  public static getMultipleProductStoreAvailabilityComplex(state: AppState, next: Dispatch): void {
    next(setStoresMapVisibilityAction(true));
    next(getUserGeoLocationAction());
    const telcoItem = state.manhattanOrder.checkoutData.steps.current.data.telcoItem;
    const accessoryItems = state.manhattanOrder.checkoutData.steps.current.data.accessoryItems;
    const requestData = [telcoItem, ...accessoryItems]
      .map(({ productId, colorId }: ICheckoutCurrentStepDataItem) => {
        return {
          item: {
            productId,
            colorId
          },
          quantity: 1,
        };
      });

    const lang = state.language.language;
    next(apiRequest(
      null,
      'POST', `${ApiUrls.STORES}/all-stores-for-multiple-products`,
      { lang },
      requestData,
      MiddlewareEntities.STORES_MAP,
      {}),
    );
    next(setLoader(true, MiddlewareEntities.STORES_MAP));
  }

  public static getOnlyStores(next: Dispatch, params: any): void {
    next(getUserGeoLocationAction());

    next(apiRequest(
      null,
      'GET', `${ApiUrls.STORES}/only-stores`,
      { ...params },
      null,
      MiddlewareEntities.STORES_MAP,
      {}),
    );
    next(setLoader(true, MiddlewareEntities.STORES_MAP));
  }

  public static getStoresAPISuccess(next: Dispatch, payload: StoresAPIRequestPayload): void {
    next(setStoresMapAction(payload.data.stores));
    next(getCitiesAction(payload.data.stores));
    next(setLoader(false, MiddlewareEntities.STORES_MAP));
    next(setApiResponseInCache(
      MiddlewareEntities.STORES_MAP + JSON.stringify(payload.meta.params),
      payload.data.stores,
      CacheTime.STORES)
    );
  }

  public static getMultipleStoresApiSuccess(store: MiddlewareAPI, next: Dispatch, payload: StoresAPIRequestPayload): void {
    const activeStoreId = store.getState().manhattanOrder.checkoutData.steps.current.data?.deliveryDetails.storeId;

    next(setStoresMapAction(payload.data.stores));
    next(getCitiesAction(payload.data.stores));
    next(setApiResponseInCache(
      MiddlewareEntities.STORES_MAP_MULTIPLE + JSON.stringify(payload.meta.params),
      payload.data.stores,
      CacheTime.STORES)
    );
    next(setSelectedStoreIdAction(activeStoreId));
  }

  public static getStoresAPIError(next: Dispatch, payload: StoresAPIRequestPayload): void {
    next(createErrorNotification(payload.data, MiddlewareEntities.STORES_MAP));
    next(setStoresMapVisibilityAction(false));
    next(setLoader(false, MiddlewareEntities.STORES_MAP));
  }

  public static setClosestStoresStatus(store: MiddlewareAPI, showClosestStoresStatus: boolean, next: Dispatch): void {
    next(setFiveClosestStatusAction());

    const { storesMap: { userGeolocation: { lat, lon } }, manhattanOrder } = store.getState();

    if (showClosestStoresStatus && (lat && lon)) {
      store.dispatch(getClosestStoresAction());
    }

    if (!showClosestStoresStatus && (lat && lon)) {
      const { productId, colorId } = manhattanOrder.checkoutData.steps.current.data;

      if (productId && colorId) {
        store.dispatch(getAllStoresMapAction(
          productId,
          colorId,
          getActiveLanguage()
        ));
      }
    }
  }

  public static getClosestStores(state: AppState, next: Dispatch): void {
    const latitude = state.storesMap.userGeolocation.lat;
    const longitude = state.storesMap.userGeolocation.lon;
    const productId = state.productData.product.item.id;
    const colorId = state.productData.productData.color;
    const distanceNumber = 5;
    const lang = getActiveLanguage();

    const params = { latitude, longitude, productId, colorId, distanceNumber, lang };

    next(apiRequest(
      null,
      'GET', `${ApiUrls.STORES}/closest-stores`,
      params,
      null,
      MiddlewareEntities.STORES_MAP_CLOSEST,
      {}),
    );
  }

  public static getClosestStoresAPISuccess(next: Dispatch, payload: StoresAPIRequestPayload): void {
    const storeIdArray = payload.data.stores.map((store: IStore) => parseInt(store.id as string));
    next(setStoreFilterAction(storeIdArray));

    if (payload.data.stores.length > 0) {
      next(setMapCenterAndZoomAction(
        { lat: payload.data.stores[0].coordinate.latitude, lng: payload.data.stores[0].coordinate.longitude },
        MapZoom.geoLocationZoom)
      );
    }

    next(setApiResponseInCache(
      MiddlewareEntities.STORES_MAP_CLOSEST + JSON.stringify(payload.meta.params),
      payload.data.stores,
      CacheTime.STORES)
    );
  }

  public static getUserGeoLocation(next: Dispatch): void {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition((position) => {
        next(setUserGeolocationAction(position.coords.latitude, position.coords.longitude));
        next(setMapCenterAndZoomAction({ lat: position.coords.latitude, lng: position.coords.longitude }, MapZoom.geoLocationZoom));
      });
    }

    next(setStoresMapVisibilityAction(true));
  }

  public static getCities(next: Dispatch, payload: IStore[]): void {
    next(setCitiesAction(Array.from(new Set(payload.map((store) => store.city)))));
  }

  public static setActiveStoreId(state: AppState, next: Dispatch, clickedOnStoreId: string | number): void {
    let { stores } = state.storesMap;
    let colorId = 0;
    let productId = 0;
    const { productData: { product: { item: { id } }, productData: { color } }, language: { language } } = state;
    const isComplex = state.manhattanOrder.checkoutData.steps.current.type === OrderTypes.COMPLEX;
    if (isCheckout()) {
      colorId = state.manhattanOrder.checkoutData.steps.current.data.colorId;
      productId = state.manhattanOrder.checkoutData.steps.current.data.productId;
    } else {
      colorId = color;
      productId = id;
    }
    stores = stores.map((store: IStore) => {
      store.showInfoWindow = store.systemId === clickedOnStoreId ? store.showInfoWindow : false;
      return store;
    });

    const activeIndex = stores.findIndex((store: IStore) => store.systemId === clickedOnStoreId);

    const { showInfoWindow, coordinate: { latitude, longitude } } = stores[activeIndex];

    stores[activeIndex].showInfoWindow = !showInfoWindow;

    next((setStoresMapAction(stores)));

    if (stores[activeIndex].showInfoWindow) {
      next(setMapCenterAndZoomAction(
        { lat: latitude, lng: longitude },
        MapZoom.zoomOnStore
      ));
      if (!isComplex) {
        next(getAvailabilityProduct(productId, colorId, clickedOnStoreId, language));
      }
    } else {
      next(setMapCenterAndZoomAction(
        { lat: latitude, lng: longitude },
        MapZoom.geoLocationZoom
      ));
    }
  }

  public static close(next: Dispatch): void {
    next(setStoresMapVisibilityAction(false));
  }

}
